import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios, { AxiosError } from 'axios';
import { useAuth } from '../contexts/AuthContext';
import AppLayout from '../components/AppLayout';
import toast from 'react-hot-toast';
import {
  TrashIcon,
  PencilIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  ClipboardDocumentListIcon,
  CalendarDaysIcon,
  ChartBarIcon,
  FolderPlusIcon,
  DocumentPlusIcon,
  ExclamationTriangleIcon,
  ArrowPathIcon,
} from '@heroicons/react/24/outline';
import { CheckCircleIcon as CheckCircleSolidIcon } from '@heroicons/react/24/solid';

interface Campaign {
  _id: string;
  name: string;
  description: string;
  isActive: boolean;
  startDate: string;
  endDate: string;
  totalItems: number;
  completedItems: number;
  createdBy?: {
    name: string;
    email: string;
  };
  createdAt: string;
}

interface ChecklistItem {
  _id: string;
  campaignId: string;
  category: string;
  title: string;
  isCompleted: boolean;
  notes: string;
  sortOrder: number;
  completedBy?: {
    name: string;
    email: string;
  };
  completedAt?: string;
}

// API Hata mesajı çıkarma yardımcı fonksiyonu
const getErrorMessage = (error: unknown): string => {
  if (axios.isAxiosError(error)) {
    const axiosError = error as AxiosError<{ message?: string }>;
    if (axiosError.response?.data?.message) {
      return axiosError.response.data.message;
    }
    if (axiosError.response?.status === 401) {
      return 'Oturum süreniz dolmuş. Lütfen tekrar giriş yapın.';
    }
    if (axiosError.response?.status === 403) {
      return 'Bu işlem için yetkiniz yok.';
    }
    if (axiosError.response?.status === 404) {
      return 'İstenen kaynak bulunamadı.';
    }
    if (axiosError.response?.status === 500) {
      return 'Sunucu hatası. Lütfen daha sonra tekrar deneyin.';
    }
    if (axiosError.code === 'ERR_NETWORK') {
      return 'Bağlantı hatası. İnternet bağlantınızı kontrol edin.';
    }
  }
  return 'Beklenmeyen bir hata oluştu.';
};

const CampaignChecklist: React.FC = () => {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';

  // State
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [selectedCampaign, setSelectedCampaign] = useState<Campaign | null>(null);
  const [checklistItems, setChecklistItems] = useState<Record<string, ChecklistItem[]>>({});
  const [loading, setLoading] = useState(true);
  const [itemsLoading, setItemsLoading] = useState(false);
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());
  const [error, setError] = useState<string | null>(null);

  // Operation states
  const [submitting, setSubmitting] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [togglingId, setTogglingId] = useState<string | null>(null);

  // Debounce ref for notes update
  const notesDebounceRef = useRef<Record<string, NodeJS.Timeout>>({});

  // Modals
  const [showCampaignModal, setShowCampaignModal] = useState(false);
  const [showItemModal, setShowItemModal] = useState(false);
  const [editingCampaign, setEditingCampaign] = useState<Campaign | null>(null);
  const [editingItem, setEditingItem] = useState<ChecklistItem | null>(null);

  // Forms
  const [campaignForm, setCampaignForm] = useState({
    name: '',
    description: '',
    startDate: '',
    endDate: '',
  });
  const [itemForm, setItemForm] = useState({
    category: '',
    title: '',
    notes: '',
  });

  // Categories suggestion
  const categoryOptions = [
    'Pazarlama & Reklam',
    'Web Sitesi & UX',
    'Fiyatlandırma & Stok Yönetimi',
    'Kategori & Navigasyon',
    'CRM, E-Posta & SMS',
    'Sosyal Medya & Duyurular',
    'Teknik & Entegrasyon',
    'Diğer',
  ];

  const loadCampaigns = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await axios.get('/campaigns', { timeout: 15000 });
      
      // Response validasyonu
      if (!Array.isArray(response.data)) {
        throw new Error('Geçersiz veri formatı');
      }
      
      setCampaigns(response.data);

      // Aktif kampanyayı varsayılan olarak seç
      const activeCampaign = response.data.find((c: Campaign) => c.isActive);
      if (activeCampaign) {
        setSelectedCampaign(activeCampaign);
      } else if (response.data.length > 0) {
        setSelectedCampaign(response.data[0]);
      } else {
        setSelectedCampaign(null);
      }
    } catch (error) {
      const message = getErrorMessage(error);
      setError(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadChecklistItems = useCallback(async (campaignId: string) => {
    if (!campaignId || typeof campaignId !== 'string') {
      return;
    }

    try {
      setItemsLoading(true);
      const response = await axios.get(`/campaigns/${campaignId}/checklist/grouped`, { 
        timeout: 15000 
      });
      
      // Response validasyonu
      if (typeof response.data !== 'object' || response.data === null) {
        throw new Error('Geçersiz veri formatı');
      }
      
      setChecklistItems(response.data);

      // Tüm kategorileri aç
      setExpandedCategories(new Set(Object.keys(response.data)));
    } catch (error) {
      toast.error(getErrorMessage(error));
      setChecklistItems({});
    } finally {
      setItemsLoading(false);
    }
  }, []);

  // Cleanup debounce timers on unmount
  useEffect(() => {
    const timers = notesDebounceRef.current;
    return () => {
      Object.values(timers).forEach(timer => clearTimeout(timer));
    };
  }, []);

  useEffect(() => {
    loadCampaigns();
  }, [loadCampaigns]);

  useEffect(() => {
    if (selectedCampaign?._id) {
      loadChecklistItems(selectedCampaign._id);
    } else {
      setChecklistItems({});
    }
  }, [selectedCampaign?._id, loadChecklistItems]);

  // ==================== KAMPANYA İŞLEMLERİ ====================

  const handleCampaignSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Form validasyonu
    const name = campaignForm.name?.trim();
    if (!name || name.length < 2) {
      toast.error('Kampanya adı en az 2 karakter olmalıdır');
      return;
    }

    // Tarih validasyonu
    if (campaignForm.startDate && campaignForm.endDate) {
      const startDate = new Date(campaignForm.startDate);
      const endDate = new Date(campaignForm.endDate);
      if (startDate > endDate) {
        toast.error('Başlangıç tarihi bitiş tarihinden sonra olamaz');
        return;
      }
    }

    try {
      setSubmitting(true);
      
      const payload = {
        name: name,
        description: campaignForm.description?.trim() || '',
        startDate: campaignForm.startDate || undefined,
        endDate: campaignForm.endDate || undefined,
      };

      if (editingCampaign) {
        await axios.put(`/campaigns/${editingCampaign._id}`, payload, { timeout: 10000 });
        toast.success('Kampanya güncellendi!');
      } else {
        await axios.post('/campaigns', payload, { timeout: 10000 });
        toast.success('Kampanya oluşturuldu!');
      }
      
      setShowCampaignModal(false);
      setEditingCampaign(null);
      setCampaignForm({ name: '', description: '', startDate: '', endDate: '' });
      await loadCampaigns();
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteCampaign = async (campaignId: string) => {
    if (!campaignId) return;
    
    if (!window.confirm('Bu kampanyayı ve tüm checklist maddelerini silmek istediğinizden emin misiniz?')) {
      return;
    }

    try {
      setDeletingId(campaignId);
      await axios.delete(`/campaigns/${campaignId}`, { timeout: 10000 });
      toast.success('Kampanya silindi!');
      setSelectedCampaign(null);
      await loadCampaigns();
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setDeletingId(null);
    }
  };

  const handleEditCampaign = (campaign: Campaign) => {
    if (!campaign) return;
    
    setEditingCampaign(campaign);
    setCampaignForm({
      name: campaign.name || '',
      description: campaign.description || '',
      startDate: campaign.startDate ? campaign.startDate.split('T')[0] : '',
      endDate: campaign.endDate ? campaign.endDate.split('T')[0] : '',
    });
    setShowCampaignModal(true);
  };

  // ==================== CHECKLIST İŞLEMLERİ ====================

  const handleItemSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!selectedCampaign?._id) {
      toast.error('Lütfen önce bir kampanya seçin');
      return;
    }

    // Form validasyonu
    const category = itemForm.category?.trim();
    const title = itemForm.title?.trim();

    if (!category) {
      toast.error('Kategori seçimi gereklidir');
      return;
    }

    if (!title || title.length < 3) {
      toast.error('Madde başlığı en az 3 karakter olmalıdır');
      return;
    }

    try {
      setSubmitting(true);
      
      const payload = {
        category,
        title,
        notes: itemForm.notes?.trim() || '',
      };

      if (editingItem) {
        await axios.put(`/campaigns/checklist/${editingItem._id}`, payload, { timeout: 10000 });
        toast.success('Madde güncellendi!');
      } else {
        await axios.post(`/campaigns/${selectedCampaign._id}/checklist`, payload, { timeout: 10000 });
        toast.success('Madde eklendi!');
      }
      
      setShowItemModal(false);
      setEditingItem(null);
      setItemForm({ category: '', title: '', notes: '' });
      await loadChecklistItems(selectedCampaign._id);
      await loadCampaigns(); // Stats güncelleme
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setSubmitting(false);
    }
  };

  const handleToggleItem = async (item: ChecklistItem) => {
    if (!item?._id || togglingId) return;

    try {
      setTogglingId(item._id);
      
      await axios.put(`/campaigns/checklist/${item._id}/status`, {
        isCompleted: !item.isCompleted,
        notes: item.notes || '',
      }, { timeout: 10000 });
      
      if (selectedCampaign?._id) {
        await loadChecklistItems(selectedCampaign._id);
        await loadCampaigns(); // Stats güncelleme
      }
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setTogglingId(null);
    }
  };

  const handleUpdateNotes = (item: ChecklistItem, notes: string) => {
    if (!item?._id) return;

    // Optimistic update
    setChecklistItems(prev => {
      const newItems = { ...prev };
      for (const category of Object.keys(newItems)) {
        newItems[category] = newItems[category].map(i =>
          i._id === item._id ? { ...i, notes } : i
        );
      }
      return newItems;
    });

    // Debounce API call
    if (notesDebounceRef.current[item._id]) {
      clearTimeout(notesDebounceRef.current[item._id]);
    }

    notesDebounceRef.current[item._id] = setTimeout(async () => {
      try {
        await axios.put(`/campaigns/checklist/${item._id}/status`, {
          isCompleted: item.isCompleted,
          notes: notes?.trim() || '',
        }, { timeout: 10000 });
      } catch (error) {
        // Revert on error
        if (selectedCampaign?._id) {
          loadChecklistItems(selectedCampaign._id);
        }
        toast.error('Not kaydedilemedi');
      }
    }, 1000);
  };

  const handleDeleteItem = async (itemId: string) => {
    if (!itemId) return;
    
    if (!window.confirm('Bu maddeyi silmek istediğinizden emin misiniz?')) {
      return;
    }

    try {
      setDeletingId(itemId);
      await axios.delete(`/campaigns/checklist/${itemId}`, { timeout: 10000 });
      toast.success('Madde silindi!');
      
      if (selectedCampaign?._id) {
        await loadChecklistItems(selectedCampaign._id);
        await loadCampaigns();
      }
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setDeletingId(null);
    }
  };

  const handleEditItem = (item: ChecklistItem) => {
    if (!item) return;
    
    setEditingItem(item);
    setItemForm({
      category: item.category || '',
      title: item.title || '',
      notes: item.notes || '',
    });
    setShowItemModal(true);
  };

  const toggleCategory = (category: string) => {
    if (!category) return;
    
    const newExpanded = new Set(expandedCategories);
    if (newExpanded.has(category)) {
      newExpanded.delete(category);
    } else {
      newExpanded.add(category);
    }
    setExpandedCategories(newExpanded);
  };

  const getProgressPercentage = (campaign: Campaign | null): number => {
    if (!campaign || !campaign.totalItems || campaign.totalItems === 0) return 0;
    const percentage = Math.round((campaign.completedItems / campaign.totalItems) * 100);
    return Math.min(100, Math.max(0, percentage));
  };

  const getCategoryStats = (items: ChecklistItem[]) => {
    if (!Array.isArray(items)) return { completed: 0, total: 0 };
    const completed = items.filter(i => i?.isCompleted).length;
    return { completed, total: items.length };
  };

  // Error State
  if (error && campaigns.length === 0) {
    return (
      <AppLayout title="Kampanya Checklist" subtitle="Kampanya kontrol listelerini yönetin">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="bg-white rounded-2xl shadow-sm p-12 text-center">
            <ExclamationTriangleIcon className="w-16 h-16 mx-auto text-red-400 mb-4" />
            <h3 className="text-lg font-semibold text-gray-700">{error}</h3>
            <button
              onClick={loadCampaigns}
              className="mt-4 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors inline-flex items-center gap-2"
            >
              <ArrowPathIcon className="w-5 h-5" />
              Tekrar Dene
            </button>
          </div>
        </div>
      </AppLayout>
    );
  }

  // Loading State
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Yükleniyor...</p>
        </div>
      </div>
    );
  }

  return (
    <AppLayout title="Kampanya Checklist" subtitle="Kampanya kontrol listelerini yönetin">

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Kampanya Seçici ve Aksiyonlar */}
        <div className="bg-white rounded-2xl shadow-sm p-6 mb-6">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            <div className="flex items-center gap-4 flex-1">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center">
                <ClipboardDocumentListIcon className="w-6 h-6 text-white" />
              </div>
              <div className="flex-1">
                <label className="block text-sm font-medium text-gray-600 mb-1">Kampanya Seçin</label>
                <select
                  value={selectedCampaign?._id || ''}
                  onChange={(e) => {
                    const campaign = campaigns.find(c => c._id === e.target.value);
                    setSelectedCampaign(campaign || null);
                  }}
                  disabled={loading}
                  className="w-full lg:w-80 px-4 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 bg-gray-50 disabled:opacity-50"
                >
                  <option value="">Kampanya seçin...</option>
                  {campaigns.map(campaign => (
                    <option key={campaign._id} value={campaign._id}>
                      {campaign.name} {campaign.isActive ? '(Aktif)' : ''}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {isAdmin && (
              <div className="flex gap-3">
                <button
                  onClick={() => {
                    setEditingCampaign(null);
                    setCampaignForm({ name: '', description: '', startDate: '', endDate: '' });
                    setShowCampaignModal(true);
                  }}
                  disabled={submitting}
                  className="px-4 py-2.5 bg-gradient-to-r from-emerald-500 to-teal-600 text-white font-medium rounded-xl hover:shadow-lg transition-all duration-300 flex items-center gap-2 disabled:opacity-50"
                >
                  <FolderPlusIcon className="w-5 h-5" />
                  Yeni Kampanya
                </button>
              </div>
            )}
          </div>

          {/* Kampanya Detay ve Progress */}
          {selectedCampaign && (
            <div className="mt-6 pt-6 border-t border-gray-100">
              <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                <div>
                  <h2 className="text-xl font-bold text-gray-900">{selectedCampaign.name}</h2>
                  {selectedCampaign.description && (
                    <p className="text-sm text-gray-500 mt-1">{selectedCampaign.description}</p>
                  )}
                  <div className="flex items-center gap-4 mt-2 text-sm text-gray-500">
                    {selectedCampaign.startDate && (
                      <span className="flex items-center gap-1">
                        <CalendarDaysIcon className="w-4 h-4" />
                        {new Date(selectedCampaign.startDate).toLocaleDateString('tr-TR')}
                        {selectedCampaign.endDate && ` - ${new Date(selectedCampaign.endDate).toLocaleDateString('tr-TR')}`}
                      </span>
                    )}
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                      selectedCampaign.isActive 
                        ? 'bg-emerald-100 text-emerald-700' 
                        : 'bg-gray-100 text-gray-600'
                    }`}>
                      {selectedCampaign.isActive ? 'Aktif' : 'Pasif'}
                    </span>
                  </div>
                </div>

                <div className="flex items-center gap-6">
                  {/* Progress */}
                  <div className="flex items-center gap-3">
                    <div className="w-32 h-3 bg-gray-200 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-gradient-to-r from-emerald-500 to-teal-500 transition-all duration-500"
                        style={{ width: `${getProgressPercentage(selectedCampaign)}%` }}
                      />
                    </div>
                    <span className="text-sm font-semibold text-gray-700">
                      {selectedCampaign.completedItems || 0}/{selectedCampaign.totalItems || 0}
                    </span>
                    <span className="text-lg font-bold text-emerald-600">
                      %{getProgressPercentage(selectedCampaign)}
                    </span>
                  </div>

                  {isAdmin && (
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleEditCampaign(selectedCampaign)}
                        disabled={submitting}
                        className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors disabled:opacity-50"
                        title="Düzenle"
                      >
                        <PencilIcon className="w-5 h-5" />
                      </button>
                      <button
                        onClick={() => handleDeleteCampaign(selectedCampaign._id)}
                        disabled={deletingId === selectedCampaign._id}
                        className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50"
                        title="Sil"
                      >
                        {deletingId === selectedCampaign._id ? (
                          <ArrowPathIcon className="w-5 h-5 animate-spin" />
                        ) : (
                          <TrashIcon className="w-5 h-5" />
                        )}
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Checklist */}
        {selectedCampaign && (
          <div className="space-y-4">
            {/* Madde Ekle Butonu */}
            {isAdmin && (
              <div className="flex justify-end">
                <button
                  onClick={() => {
                    setEditingItem(null);
                    setItemForm({ category: '', title: '', notes: '' });
                    setShowItemModal(true);
                  }}
                  disabled={submitting}
                  className="px-4 py-2 bg-white border border-emerald-200 text-emerald-700 font-medium rounded-xl hover:bg-emerald-50 transition-colors flex items-center gap-2 disabled:opacity-50"
                >
                  <DocumentPlusIcon className="w-5 h-5" />
                  Yeni Madde Ekle
                </button>
              </div>
            )}

            {/* Loading State for Items */}
            {itemsLoading ? (
              <div className="bg-white rounded-2xl shadow-sm p-12 text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600 mx-auto"></div>
                <p className="mt-4 text-gray-500">Checklist yükleniyor...</p>
              </div>
            ) : Object.keys(checklistItems).length === 0 ? (
              <div className="bg-white rounded-2xl shadow-sm p-12 text-center">
                <ClipboardDocumentListIcon className="w-16 h-16 mx-auto text-gray-300 mb-4" />
                <h3 className="text-lg font-semibold text-gray-700">Henüz madde eklenmemiş</h3>
                <p className="text-gray-500 mt-2">
                  {isAdmin 
                    ? 'Yukarıdaki "Yeni Madde Ekle" butonunu kullanarak başlayın.'
                    : 'Admin tarafından checklist maddeleri eklenecek.'}
                </p>
              </div>
            ) : (
              Object.entries(checklistItems).map(([category, items]) => {
                if (!Array.isArray(items)) return null;
                
                const stats = getCategoryStats(items);
                const isExpanded = expandedCategories.has(category);

                return (
                  <div key={category} className="bg-white rounded-2xl shadow-sm overflow-hidden">
                    {/* Kategori Header */}
                    <button
                      onClick={() => toggleCategory(category)}
                      className="w-full px-6 py-4 flex items-center justify-between bg-gradient-to-r from-gray-50 to-white hover:from-gray-100 hover:to-gray-50 transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                          stats.completed === stats.total 
                            ? 'bg-emerald-100' 
                            : 'bg-amber-100'
                        }`}>
                          {stats.completed === stats.total ? (
                            <CheckCircleSolidIcon className="w-6 h-6 text-emerald-600" />
                          ) : (
                            <ChartBarIcon className="w-6 h-6 text-amber-600" />
                          )}
                        </div>
                        <div className="text-left">
                          <h3 className="text-lg font-semibold text-gray-900">{category}</h3>
                          <p className="text-sm text-gray-500">
                            {stats.completed}/{stats.total} tamamlandı
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="w-24 h-2 bg-gray-200 rounded-full overflow-hidden">
                          <div
                            className={`h-full transition-all duration-300 ${
                              stats.completed === stats.total 
                                ? 'bg-emerald-500' 
                                : 'bg-amber-500'
                            }`}
                            style={{ width: stats.total > 0 ? `${(stats.completed / stats.total) * 100}%` : '0%' }}
                          />
                        </div>
                        {isExpanded ? (
                          <ChevronUpIcon className="w-5 h-5 text-gray-400" />
                        ) : (
                          <ChevronDownIcon className="w-5 h-5 text-gray-400" />
                        )}
                      </div>
                    </button>

                    {/* Maddeler */}
                    {isExpanded && (
                      <div className="divide-y divide-gray-100">
                        {items.map((item) => {
                          if (!item?._id) return null;
                          
                          return (
                            <div
                              key={item._id}
                              className={`px-6 py-4 flex items-start gap-4 transition-colors ${
                                item.isCompleted ? 'bg-emerald-50/50' : 'hover:bg-gray-50'
                              }`}
                            >
                              {/* Checkbox */}
                              <button
                                onClick={() => handleToggleItem(item)}
                                disabled={togglingId === item._id}
                                className="mt-0.5 flex-shrink-0 disabled:opacity-50"
                              >
                                {togglingId === item._id ? (
                                  <ArrowPathIcon className="w-6 h-6 text-emerald-500 animate-spin" />
                                ) : item.isCompleted ? (
                                  <CheckCircleSolidIcon className="w-6 h-6 text-emerald-500" />
                                ) : (
                                  <div className="w-6 h-6 border-2 border-gray-300 rounded-full hover:border-emerald-500 transition-colors" />
                                )}
                              </button>

                              {/* İçerik */}
                              <div className="flex-1 min-w-0">
                                <p className={`text-sm font-medium ${
                                  item.isCompleted ? 'text-gray-500 line-through' : 'text-gray-900'
                                }`}>
                                  {item.title}
                                </p>
                                
                                {/* Notlar */}
                                <div className="mt-2">
                                  <input
                                    type="text"
                                    value={item.notes || ''}
                                    onChange={(e) => handleUpdateNotes(item, e.target.value)}
                                    placeholder="Not ekle..."
                                    maxLength={500}
                                    className="w-full text-sm text-gray-500 bg-transparent border-0 border-b border-transparent hover:border-gray-200 focus:border-emerald-500 focus:ring-0 px-0 py-1 placeholder-gray-400"
                                  />
                                </div>

                                {/* Tamamlayan Bilgisi */}
                                {item.isCompleted && item.completedBy && (
                                  <p className="text-xs text-gray-400 mt-1">
                                    ✓ {item.completedBy.name} tarafından{' '}
                                    {item.completedAt && new Date(item.completedAt).toLocaleString('tr-TR')}
                                  </p>
                                )}
                              </div>

                              {/* Admin Aksiyonları */}
                              {isAdmin && (
                                <div className="flex gap-1 flex-shrink-0">
                                  <button
                                    onClick={() => handleEditItem(item)}
                                    disabled={submitting}
                                    className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors disabled:opacity-50"
                                    title="Düzenle"
                                  >
                                    <PencilIcon className="w-4 h-4" />
                                  </button>
                                  <button
                                    onClick={() => handleDeleteItem(item._id)}
                                    disabled={deletingId === item._id}
                                    className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50"
                                    title="Sil"
                                  >
                                    {deletingId === item._id ? (
                                      <ArrowPathIcon className="w-4 h-4 animate-spin" />
                                    ) : (
                                      <TrashIcon className="w-4 h-4" />
                                    )}
                                  </button>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>
        )}

        {!selectedCampaign && campaigns.length === 0 && (
          <div className="bg-white rounded-2xl shadow-sm p-12 text-center">
            <FolderPlusIcon className="w-16 h-16 mx-auto text-gray-300 mb-4" />
            <h3 className="text-lg font-semibold text-gray-700">Henüz kampanya yok</h3>
            <p className="text-gray-500 mt-2">
              {isAdmin 
                ? 'İlk kampanyanızı oluşturmak için "Yeni Kampanya" butonunu kullanın.'
                : 'Admin tarafından kampanya oluşturulacak.'}
            </p>
          </div>
        )}
      </div>

      {/* Kampanya Modal */}
      {showCampaignModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-md w-full p-6">
            <h3 className="text-xl font-semibold text-gray-900 mb-6">
              {editingCampaign ? 'Kampanya Düzenle' : 'Yeni Kampanya'}
            </h3>

            <form onSubmit={handleCampaignSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Kampanya Adı *</label>
                <input
                  type="text"
                  value={campaignForm.name}
                  onChange={(e) => setCampaignForm({ ...campaignForm, name: e.target.value })}
                  required
                  minLength={2}
                  maxLength={200}
                  placeholder="Örn: Yılbaşı Kampanyası 2024"
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Açıklama</label>
                <textarea
                  value={campaignForm.description}
                  onChange={(e) => setCampaignForm({ ...campaignForm, description: e.target.value })}
                  rows={3}
                  maxLength={1000}
                  placeholder="Kampanya hakkında kısa açıklama..."
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Başlangıç Tarihi</label>
                  <input
                    type="date"
                    value={campaignForm.startDate}
                    onChange={(e) => setCampaignForm({ ...campaignForm, startDate: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Bitiş Tarihi</label>
                  <input
                    type="date"
                    value={campaignForm.endDate}
                    onChange={(e) => setCampaignForm({ ...campaignForm, endDate: e.target.value })}
                    min={campaignForm.startDate || undefined}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  />
                </div>
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  type="submit"
                  disabled={submitting}
                  className="flex-1 bg-gradient-to-r from-emerald-500 to-teal-600 text-white font-semibold py-3 px-4 rounded-xl hover:shadow-lg transition-all duration-300 disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {submitting && <ArrowPathIcon className="w-5 h-5 animate-spin" />}
                  {editingCampaign ? 'Güncelle' : 'Oluştur'}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowCampaignModal(false);
                    setEditingCampaign(null);
                  }}
                  disabled={submitting}
                  className="flex-1 bg-gray-200 text-gray-700 font-semibold py-3 px-4 rounded-xl hover:bg-gray-300 transition-colors disabled:opacity-50"
                >
                  İptal
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Checklist Item Modal */}
      {showItemModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-lg w-full p-6">
            <h3 className="text-xl font-semibold text-gray-900 mb-6">
              {editingItem ? 'Madde Düzenle' : 'Yeni Madde Ekle'}
            </h3>

            <form onSubmit={handleItemSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Kategori *</label>
                <select
                  value={itemForm.category}
                  onChange={(e) => setItemForm({ ...itemForm, category: e.target.value })}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500"
                >
                  <option value="">Kategori seçin...</option>
                  {categoryOptions.map(cat => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Madde Başlığı *</label>
                <textarea
                  value={itemForm.title}
                  onChange={(e) => setItemForm({ ...itemForm, title: e.target.value })}
                  required
                  minLength={3}
                  maxLength={1000}
                  rows={3}
                  placeholder="Kontrol edilecek maddeyi yazın..."
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Not (Opsiyonel)</label>
                <input
                  type="text"
                  value={itemForm.notes}
                  onChange={(e) => setItemForm({ ...itemForm, notes: e.target.value })}
                  maxLength={500}
                  placeholder="Ek açıklama veya not..."
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500"
                />
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  type="submit"
                  disabled={submitting}
                  className="flex-1 bg-gradient-to-r from-emerald-500 to-teal-600 text-white font-semibold py-3 px-4 rounded-xl hover:shadow-lg transition-all duration-300 disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {submitting && <ArrowPathIcon className="w-5 h-5 animate-spin" />}
                  {editingItem ? 'Güncelle' : 'Ekle'}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowItemModal(false);
                    setEditingItem(null);
                  }}
                  disabled={submitting}
                  className="flex-1 bg-gray-200 text-gray-700 font-semibold py-3 px-4 rounded-xl hover:bg-gray-300 transition-colors disabled:opacity-50"
                >
                  İptal
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </AppLayout>
  );
};

export default CampaignChecklist;
